import { apiClient } from "@/api/apiClient";
import store from "@/store/index";
import { toApiDistanceUnit } from "@/utils/assetImportDistanceUnit";
import axios from "axios";

/**
 * Retrieves bucket information for Design3D for a specific site and type.
 *
 * @param {string} siteId - The ID of the site.
 * @param {string} type - The type of design data (e.g., 'pointcloud', 'design', 'lineworks', 'drawing2dImage').
 * @returns {Promise<Object>} The response containing bucket information.
 * @throws {Error} If the request fails.
 */
export const getDesign3dBucketInfo = async (siteId, type) => {
	try {
		const res = await axios.get(
			`${import.meta.env.VITE_API_BASE}/sites/bucketInfo/${type}/${siteId}`,
			{
				auth: store.state.authObject,
			},
		);
		return res;
	} catch (error) {
		console.error(`Failed to get bucket info for ${type}:`, error);
		throw new Error(`Failed to get bucket info: ${error.message}`);
	}
};

/**
 * Import a LandXML / DXF design from an EB Platform node (v2-server).
 * Creates the design row and starts processing in the background; the
 * result is tracked through `processing_status` on the design list.
 * @param {string} siteId - The site ID
 * @param {Object} body - Import parameters
 * @param {string} body.name - Name of the design
 * @param {string} body.date - Date of the design (YYYY-MM-DD)
 * @param {string} body.time - Time of the design (HH:mm:ss)
 * @param {string} body.bucket_id - Bucket ID
 * @param {string} body.node_id - Node ID
 * @returns {Promise<{design_id: number}>}
 */
export const importDesign = async (
	siteId,
	{ name, date, time, bucket_id, node_id },
) => {
	const res = await apiClient.v2.api[":siteId"].design.$post({
		param: { siteId },
		json: {
			name,
			date,
			time,
			source: { type: "ebPlatformNode", bucketId: bucket_id, nodeId: node_id },
			siteDistanceUnit: toApiDistanceUnit(store.state.site?.units?.distance),
		},
	});
	if (!res.ok) {
		throw new Error(`Failed to import design: ${res.status}`);
	}
	const { id } = await res.json();
	return { design_id: id };
};
